// API shapes used by the shell and the sign-in screen. They are the server contract (packages/contracts, zod 4) — the
// names below are kept from the first provisional version so the importers did not have to change. Screens import their
// own response schemas straight from '@aiax-hub/contracts' in their api.ts.
import { z } from 'zod';
import {
  AuthOptionsResponse,
  FooterStatusResponse,
  LoginResponse,
  MeResponse,
  NotificationItem,
  NotificationListResponse,
  OkResponse,
  ReadAllResponse,
  ReauthResponse,
  SearchResponse,
  TeamsTreeResponse,
  type LoginRequest,
  type MfaRequest,
  type PasswordChangeRequest,
  type ReauthRequest,
  type SetupRequest,
} from '@aiax-hub/contracts';

/**
 * Error envelope of every /api/v1/* failure: { error: { code, message, requestId, details? } } (contracts ErrorEnvelope).
 * Read leniently on purpose: a proxy or an older server may answer with a code outside ERROR_CODES or without a request
 * id, and the screen must still show the code instead of a generic HTTP error.
 */
export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

/** GET /api/v1/auth/options — what the sign-in screen may offer before anyone is signed in. */
export const AuthOptionsSchema = AuthOptionsResponse;
export type AuthOptions = AuthOptionsResponse;

/** POST /api/v1/auth/login · /mfa (and the status-shaped answers of /password and /setup). */
export const LoginResultSchema = LoginResponse;
export type LoginResult = LoginResponse;
export type LoginStatus = LoginResult['status'];
export type { LoginRequest, MfaRequest, PasswordChangeRequest, SetupRequest, ReauthRequest };

/** GET /api/v1/auth/me — the signed-in person, held roles, EFFECTIVE permissions (every key), gates, session, org values. */
export const AuthMeSchema = MeResponse;
export type AuthMe = MeResponse;

/** GET /api/v1/status — footer "● 수집 서버 연결됨 · 마지막 기록 2분 전" (status.footer). */
export const FooterStatusSchema = FooterStatusResponse;
export type FooterStatus = z.infer<typeof FooterStatusResponse>;

/** GET /api/v1/notifications · POST …/:id/read · POST …/read-all — kind + params; the web words title/body (notif.<kind>.*). */
export const NotificationListSchema = NotificationListResponse;
export type NotificationList = z.infer<typeof NotificationListResponse>;
export type AppNotification = NotificationItem;
export { NotificationItem, OkResponse, ReadAllResponse };

/** GET /api/v1/search — people and records the reader may see (screens and glossary terms are searched in the browser). */
export const SearchSchema = SearchResponse;
export type SearchResult = SearchResponse;

/** GET /api/v1/teams — team tree for the header team filter (`pickable` already applies small-group protection). */
export const TeamsTreeSchema = TeamsTreeResponse;
export type TeamsTree = z.infer<typeof TeamsTreeResponse>;

/** POST /api/v1/auth/reauth — step-up before opening someone else's text. */
export const ReauthSchema = ReauthResponse;
